import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";

// Schema de validação
const formSchema = z.object({
  experienceRating: z.string().min(1, "por favor, avalie sua experiência"),
  device: z.string().min(1, "por favor, selecione o dispositivo"),
  intuitiveness: z.string().min(1, "por favor, avalie a intuitividade"),
  darkMode: z.string().min(1, "por favor, responda sobre o dark mode"),
  missionsMotivation: z.string().min(1, "por favor, responda sobre as missões"),
  prizeSuggestions: z.string().optional(),
  feedbackText: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

// Configurações do formulário
const FORM_CONFIG = {
  color: '#FECF04',
  logo: '/thenews.webp',
  title: 'avaliação da nova interface do streak'
};

// IDs dos campos
const FIELD_IDS = {
  experienceRating: "AyYuvSLV4ycMS2DyoSwNo",
  device: "N-XViShFWmMExLCS9fc6j",
  intuitiveness: "g8zU9YyJmwmp4HPNqM3j6",
  darkMode: "HCHK40lROSpESRVrlwaXY",
  missionsMotivation: "em7Ahvvb07PGrJW0z4BNW",
  prizeSuggestions: "6tdRm9JuoG-bJ3s49V_Ee",
  feedbackText: "CAYJBiM_g3oKanKjc39pH",
};

function OptionButton({ value, selected, onClick, children, className = "" }: { value: string, selected: boolean, onClick: (v: string) => void, children: React.ReactNode, className?: string }) {
  let letra = null;
  let texto = children;
  if (Array.isArray(children) && children.length === 2 && typeof children[0] === 'object') {
    letra = children[0];
    texto = children[1];
  }
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={`flex items-center justify-center px-3 py-2 rounded-lg border text-sm font-sm transition-colors
        ${selected ? 'bg-[var(--newsletter-color)] text-white border-[var(--newsletter-color)]' : 'bg-gray-100 text-gray-900 border-gray-200 hover:bg-[var(--newsletter-color)]/10'}
        ${className}
      `}
      style={{ minHeight: 36 }}
    >
      {letra && (
        <span className={`flex items-center justify-center w-7 h-7 rounded-md font-bold text-base mr-1
          ${selected ? 'bg-white text-[var(--newsletter-color)]' : 'bg-gray-300 text-gray-700'}`}
        >
          {letra.props.children}
        </span>
      )}
      <span className="flex-1 text-left whitespace-pre-line">{texto}</span>
    </button>
  );
}

export function StreakForm() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const emailParam = searchParams.get('email') || "";
  const ratingParam = searchParams.get('rating') || "";
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      experienceRating: ratingParam,
      device: "",
      intuitiveness: "",
      darkMode: "",
      missionsMotivation: "",
      prizeSuggestions: "",
      feedbackText: "",
    },
    mode: "onChange"
  });

  useEffect(() => {
    const ratingParamValue = searchParams.get('rating');
    if (ratingParamValue) {
      form.setValue('experienceRating', ratingParamValue);
    }
  }, [searchParams, form]);

  useEffect(() => {
    const subscription = form.watch((values) => {
      const requiredFields = ['experienceRating', 'device', 'intuitiveness', 'darkMode', 'missionsMotivation'];
      const totalFields = requiredFields.length;
      const filledFields = requiredFields.filter(field => values[field as keyof FormValues]).length;
      const progress = (filledFields / totalFields) * 100;
      setProgress(progress);
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const onSubmit = async (data: FormValues) => {
    try {
      setLoading(true);
      setError("");

      const formData = {
        formId: "ySMGaSDoQBPvUfZi2Miik",
        userId: emailParam,
        metadata: {
          source: "streak_interface_feedback",
          campaign: "feedback_junho2025"
        },
        values: [
          { fieldId: FIELD_IDS.experienceRating, value: data.experienceRating },
          { fieldId: FIELD_IDS.device, value: data.device },
          { fieldId: FIELD_IDS.intuitiveness, value: data.intuitiveness },
          { fieldId: FIELD_IDS.darkMode, value: data.darkMode },
          { fieldId: FIELD_IDS.missionsMotivation, value: data.missionsMotivation },
          { fieldId: FIELD_IDS.prizeSuggestions, value: data.prizeSuggestions || "" },
          { fieldId: FIELD_IDS.feedbackText, value: data.feedbackText || "" },
        ]
      };

      const response = await fetch('https://backend.testeswaffle.org/forms/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Erro ao enviar formulário' }));
        throw new Error(errorData.message || 'Erro ao enviar formulário');
      }

      setIsSuccess(true);
      form.reset();
      setTimeout(() => {
        setIsSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Erro:', error);
      setError(error instanceof Error ? error.message : 'Ocorreu um erro ao enviar o formulário. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (values: FormValues) => {
    return onSubmit(values);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className={`animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-[${FORM_CONFIG.color}]`}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center pt-8 relative pb-20" style={{ '--newsletter-color': FORM_CONFIG.color } as React.CSSProperties}>
      <div className="w-full max-w-5xl p-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex-1 flex flex-col justify-center text-center"
        >
          <img 
            src={FORM_CONFIG.logo}
            alt={FORM_CONFIG.title}
            className="w-40 mx-auto mb-6"
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="space-y-8"
          >
            <h1 className="text-4xl font-bold text-gray-900">
              {FORM_CONFIG.title}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">queremos saber sua opinião sobre a nova interface do streak e como podemos melhorar sua experiência.</p>

            {isSuccess ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="bg-white rounded-3xl p-12 shadow-sm border border-gray-100 max-w-3xl mx-auto text-center"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", duration: 0.5 }}
                  className="flex items-center justify-center w-20 h-20 rounded-full bg-[var(--newsletter-color)] text-white mx-auto mb-6"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </motion.div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">resposta enviada com sucesso! 🎉</h2>
                <p className="text-xl text-gray-700 font-['Inter']">
                  obrigado por participar da nossa pesquisa!
                </p>
              </motion.div>
            ) : (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100 max-w-3xl mx-auto"
                  >
                    <div className="space-y-6">
                      <FormField
                        control={form.control}
                        name="experienceRating"
                        render={({ field }) => (
                          <FormItem>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Como você avalia a experiência geral na nova interface do streak?</h3>
                            <FormControl>
                              <div>
                                <div className="flex flex-wrap gap-2 justify-center mb-2">
                                  {["1", "2", "3", "4", "5"].map((num) => (
                                    <OptionButton
                                      key={num}
                                      value={num}
                                      selected={field.value === num}
                                      onClick={field.onChange}
                                    >
                                      {num}
                                    </OptionButton>
                                  ))}
                                </div>
                                <div className="flex justify-between text-xs text-gray-500 px-2">
                                  <span>Ruim</span>
                                  <span>Excelente</span>
                                </div>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="device"
                        render={({ field }) => (
                          <FormItem>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Você acessou pelo celular ou computador?</h3>
                            <FormControl>
                              <div className="flex flex-col md:flex-row gap-3 w-full justify-center">
                                {["Celular", "Computador"].map((option, idx) => (
                                  <OptionButton
                                    key={option}
                                    value={option}
                                    selected={field.value === option}
                                    onClick={field.onChange}
                                    className="w-full md:w-auto justify-start"
                                  >
                                    <span className="font-bold mr-2">{String.fromCharCode(65 + idx)}</span> {option}
                                  </OptionButton>
                                ))}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="intuitiveness"
                        render={({ field }) => (
                          <FormItem>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">O quão intuitivo/fácil foi para você mexer na nova interface?</h3>
                            <FormControl>
                              <div className="flex flex-col gap-3 w-full">
                                {["Muito intuitivo", "Intuitivo", "Pouco intuitivo", "Nada intuitivo"].map((option, idx) => (
                                  <OptionButton
                                    key={option}
                                    value={option}
                                    selected={field.value === option}
                                    onClick={field.onChange}
                                    className="w-full justify-start"
                                  >
                                    <span className="font-bold mr-2">{String.fromCharCode(65 + idx)}</span> {option}
                                  </OptionButton>
                                ))}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="darkMode"
                        render={({ field }) => (
                          <FormItem>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Você gostaria de uma opção dark mode da aplicação?</h3>
                            <FormControl>
                              <div className="flex flex-col md:flex-row gap-3 w-full justify-center">
                                {["Sim", "Não"].map((option, idx) => (
                                  <OptionButton
                                    key={option}
                                    value={option}
                                    selected={field.value === option}
                                    onClick={field.onChange}
                                    className="w-full md:w-auto justify-start"
                                  >
                                    <span className="font-bold mr-2">{String.fromCharCode(65 + idx)}</span> {option}
                                  </OptionButton>
                                ))}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="missionsMotivation"
                        render={({ field }) => (
                          <FormItem>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">As novas missões de streak te deixaram mais animado para manter sua ofensiva?</h3>
                            <FormControl>
                              <div className="flex flex-col gap-3 w-full">
                                {["Sim", "Indiferente", "Não"].map((option, idx) => (
                                  <OptionButton
                                    key={option}
                                    value={option}
                                    selected={field.value === option}
                                    onClick={field.onChange}
                                    className="w-full justify-start"
                                  >
                                    <span className="font-bold mr-2">{String.fromCharCode(65 + idx)}</span> {option}
                                  </OptionButton>
                                ))}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="prizeSuggestions"
                        render={({ field }) => (
                          <FormItem>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Quais outros prêmios você gostaria de ver quando completar novas missões do streak?</h3>
                            <FormControl>
                              <textarea
                                className="w-full border rounded-lg p-2 min-h-[80px]"
                                {...field}
                                value={field.value || ''}
                                placeholder="Sugestões de prêmios..."
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="feedbackText"
                        render={({ field }) => (
                          <FormItem>
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Nos conte o que achou da nova interface, é o momento de nos dar o seu feedback e sugestões de como podemos melhorar.</h3>
                            <FormControl>
                              <textarea
                                className="w-full border rounded-lg p-2 min-h-[80px]"
                                {...field}
                                value={field.value || ''}
                                placeholder="Seu feedback é muito importante..."
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-[var(--newsletter-color)] hover:bg-[var(--newsletter-color)]/90 text-white px-6 py-4 text-base rounded-lg transition-colors mt-6"
                      disabled={loading}
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-4 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        'enviar'
                      )}
                    </Button>
                  </motion.div>

                  <div className="mt-auto flex justify-center items-center space-x-4">
                    <div className="h-2 w-48 bg-gray-200 rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-[var(--newsletter-color)]"
                        initial={{ width: "0%" }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                    <span className="text-base md:text-lg text-gray-500">
                      {Math.round(progress)}% completo
                    </span>
                  </div>

                  {error && (
                    <motion.p
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-500 text-center mt-2"
                    >
                      {error}
                    </motion.p>
                  )}
                </form>
              </Form>
            )}
          </motion.div>
        </motion.div>

        <div className="fixed left-0 bottom-0 w-full h-2 bg-gray-200 z-50">
          <motion.div 
            className="h-full bg-[var(--newsletter-color)]"
            initial={{ width: "0%" }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>
    </div>
  );
} 